import React, { useState, useEffect } from 'react';
import { 
  Button, 
  MessageBar, 
  Spinner, 
  Text,
  Card,
  CardHeader,
  Body1Strong,
  Caption1,
  makeStyles,
  tokens
} from '@fluentui/react-components';
import { 
  CheckmarkCircle24Regular, 
  ErrorCircle24Regular, 
  Info24Regular,
  Shield24Regular 
} from '@fluentui/react-icons';
import * as microsoftTeams from '@microsoft/teams-js';
import config from '../../config';
import { getTeamsEnvironment, shouldUseTeamsAuth, logTeamsEnvironment } from '../../utils/teamsEnvironment';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingVerticalXL,
    maxWidth: '600px',
    margin: '0 auto',
  },
  card: {
    marginBottom: tokens.spacingVerticalM,
  },
  actionButton: {
    marginRight: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
  },
  stepsList: {
    paddingLeft: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalS,
  },
  step: {
    marginBottom: tokens.spacingVerticalXS,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalM,
  },
});

const ConsentManager = ({ 
  error, 
  onRetry, 
  tenantId, 
  clientId = config.clientId, // Use environment variable
  onConsentCompleted,
  missingScopes = []
}) => {
  const styles = useStyles();
  const [consentStatus, setConsentStatus] = useState('pending');
  const [isLoading, setIsLoading] = useState(false);
  const [teamsInitialized, setTeamsInitialized] = useState(false);
  
  // Initialize Teams SDK if in Teams context
  useEffect(() => {
    if (window.microsoftTeams) {
      try {
        microsoftTeams.app.initialize().then(() => {
          console.log('Teams SDK initialized in ConsentManager');
          setTeamsInitialized(true);
        }).catch((error) => {
          console.log('Teams SDK initialization failed:', error);
        });
      } catch (error) {
        console.log('Teams SDK not available:', error);
      }
    }
  }, []);
  
  const teamsWebClientId = config.teamsWebClientId; // Use environment variable
  
  const generateConsentUrls = () => {
    const actualTenantId = tenantId && tenantId !== 'organizations' ? tenantId : 'common';
    const redirectUri = encodeURIComponent(window.location.origin + '/auth-end.html');
    
    return {
      teamsConsent: `https://login.microsoftonline.com/${actualTenantId}/adminconsent?client_id=${teamsWebClientId}&redirect_uri=${redirectUri}&state=teams_consent`,
      appConsent: `https://login.microsoftonline.com/${actualTenantId}/adminconsent?client_id=${clientId}&redirect_uri=${redirectUri}&state=app_consent`,
    };
  };

  const consentUrls = generateConsentUrls();

  const analyzeError = (error) => {
    if (!error) return { type: 'unknown', needsTeams: false, needsApp: false };

    const errorCode = error.errorCode || error.error || '';
    const errorMessage = error.errorMessage || error.error_description || error.message || '';

    // Check for specific consent issues
    const needsTeams = errorMessage.includes(teamsWebClientId) || 
                      errorMessage.includes('Teams Web Client') ||
                      errorCode === 'teams_consent_required';
    
    const needsApp = errorMessage.includes(clientId) || 
                    errorCode === 'consent_required' || 
                    errorCode === 'admin_consent_required' ||
                    errorCode === 'interaction_required';

    let type = 'unknown';
    if (needsTeams && needsApp) type = 'both';
    else if (needsTeams) type = 'teams';
    else if (needsApp) type = 'app';
    else if (errorCode === 'invalid_grant') type = 'app'; // Most likely app consent issue

    return { type, needsTeams, needsApp, errorCode, errorMessage };
  };

  const errorAnalysis = analyzeError(error);

  const handleConsentClick = async (url, type) => {
    setIsLoading(true);
    setConsentStatus(`requesting-${type}`);
    
    try {
      // Log environment for debugging
      const teamsEnv = logTeamsEnvironment();
      
      // Check if we should use Teams SDK authentication
      if (shouldUseTeamsAuth() && teamsInitialized) {
        // Use Teams SDK authentication for desktop/mobile clients
        console.log('Using Teams SDK authentication for consent');
        
        try {
          microsoftTeams.authentication.authenticate({
            url: url,
            width: 700,
            height: 800,
            successCallback: (result) => {
              console.log('Teams auth success:', result);
              setIsLoading(false);
              setConsentStatus('completed');
              
              // Wait a moment then trigger retry
              setTimeout(() => {
                onConsentCompleted && onConsentCompleted();
                onRetry && onRetry();
              }, 2000);
            },
            failureCallback: (error) => {
              console.error('Teams auth failed:', error);
              setIsLoading(false);
              setConsentStatus('error');
              
              // Try fallback method
              if (window.confirm(`Teams authentication failed. Try opening in browser?\n\nError: ${error}`)) {
                window.open(url, '_blank');
              }
            }
          });
        } catch (teamsError) {
          console.error('Teams SDK auth error:', teamsError);
          // Fall back to window.open
          fallbackToWindowOpen(url, type);
        }
      } else {
        // Not in Teams context, use regular window.open
        fallbackToWindowOpen(url, type);
      }
    } catch (error) {
      console.error('Error in consent flow:', error);
      setIsLoading(false);
      setConsentStatus('error');
    }
  };
  
  const fallbackToWindowOpen = (url, type) => {
    try {
      // Enhanced popup options for better z-index and focus
      const popupFeatures = [
        'width=700',
        'height=800',
        'scrollbars=yes',
        'resizable=yes',
        'toolbar=no',
        'menubar=no',
        'location=no',
        'status=no',
        'top=50',
        'left=50',
        'directories=no'
      ].join(',');
      
      // Open consent window with enhanced settings
      const consentWindow = window.open(url, `consent_${type}_${Date.now()}`, popupFeatures);
      
      if (!consentWindow) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }
      
      // Force focus immediately and repeatedly
      consentWindow.focus();
      
      // Keep focusing the popup to ensure it stays on top
      const focusInterval = setInterval(() => {
        if (consentWindow && !consentWindow.closed) {
          try {
            consentWindow.focus();
          } catch (e) {
            // Ignore focus errors from cross-origin restrictions
          }
        } else {
          clearInterval(focusInterval);
        }
      }, 1000);
      
      // Monitor consent window closure
      const checkClosed = setInterval(() => {
        if (consentWindow.closed) {
          clearInterval(checkClosed);
          clearInterval(focusInterval);
          setIsLoading(false);
          setConsentStatus('completed');
          
          // Wait a moment then trigger retry
          setTimeout(() => {
            onConsentCompleted && onConsentCompleted();
            onRetry && onRetry();
          }, 2000);
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error opening consent popup:', error);
      setIsLoading(false);
      setConsentStatus('error');
      
      // Final fallback: redirect in same window if popup fails
      if (window.confirm(`Popup was blocked. Redirect to consent page instead?\n\nThis will navigate away from this page.`)) {
        window.location.href = url;
      }
    }
  };

  const getErrorIcon = () => {
    switch (errorAnalysis.type) {
      case 'teams': return <Shield24Regular color={tokens.colorPaletteBlueBackground3} />;
      case 'app': return <ErrorCircle24Regular color={tokens.colorPaletteRedBackground3} />;
      case 'both': return <Info24Regular color={tokens.colorPaletteYellowBackground3} />;
      default: return <ErrorCircle24Regular color={tokens.colorPaletteRedBackground3} />;
    }
  };

  const getErrorTitle = () => {
    switch (errorAnalysis.type) {
      case 'teams': return 'Teams Web Client Approval Required';
      case 'app': return 'App Approval Required';
      case 'both': return 'Multiple Approvals Required';
      default: return 'Authentication Issue';
    }
  };

  const getErrorMessage = () => {
    switch (errorAnalysis.type) {
      case 'teams': 
        return 'Microsoft Teams Web Client needs admin approval in your organization before this app can authenticate users.';
      case 'app': 
        return 'This application needs admin approval in your organization before it can access your data.';
      case 'both': 
        return 'Both Microsoft Teams Web Client and this application need admin approval from your organization.';
      default: 
        return `Authentication failed: ${errorAnalysis.errorMessage}`;
    }
  };

  const getSteps = () => {
    const steps = [];
    
    if (errorAnalysis.needsTeams) {
      steps.push('Admin approves Microsoft Teams Web Client');
    }
    
    if (errorAnalysis.needsApp) {
      steps.push('Admin approves IzzyGo application');
    }
    
    if (steps.length === 0) {
      steps.push('Contact your IT administrator with the error details');
      steps.push('Try again after the issue is resolved');
    } else {
      steps.push('Try signing in again after approval');
    }
    
    return steps;
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <Card className={styles.card}>
          <CardHeader
            header={<Body1Strong>Processing Consent...</Body1Strong>}
            description={<Caption1>Please complete the approval process in the popup window.</Caption1>}
          />
          <div className={styles.loadingContainer}>
            <Spinner size="small" />
            <Text>Waiting for admin consent...</Text>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <CardHeader
          image={getErrorIcon()}
          header={<Body1Strong>{getErrorTitle()}</Body1Strong>}
          description={<Caption1>{getErrorMessage()}</Caption1>}
        />
        
        <div style={{ padding: tokens.spacingVerticalM }}>
          <Text weight="semibold">Required Steps:</Text>
          <ol className={styles.stepsList}>
            {getSteps().map((step, index) => (
              <li key={index} className={styles.step}>
                <Text size={200}>{step}</Text>
              </li>
            ))}
          </ol>
        </div>

        <div style={{ padding: tokens.spacingVerticalS }}>
          {errorAnalysis.needsTeams && (
            <Button 
              appearance="primary"
              icon={<Shield24Regular />}
              className={styles.actionButton}
              onClick={() => handleConsentClick(consentUrls.teamsConsent, 'teams')}
            >
              Approve Teams Web Client
            </Button>
          )}
          
          {errorAnalysis.needsApp && (
            <Button 
              appearance="primary"
              icon={<CheckmarkCircle24Regular />}
              className={styles.actionButton}
              onClick={() => handleConsentClick(consentUrls.appConsent, 'app')}
            >
              Approve IzzyGo App
            </Button>
          )}
          
          <Button 
            appearance="secondary"
            className={styles.actionButton}
            onClick={onRetry}
          >
            Try Again
          </Button>
        </div>
      </Card>

      {consentStatus === 'completed' && (
        <MessageBar intent="success">
          <Text>Consent process completed! Retrying authentication...</Text>
        </MessageBar>
      )}
      
      {errorAnalysis.errorCode && (
        <Card className={styles.card}>
          <CardHeader
            header={<Body1Strong>Technical Details</Body1Strong>}
            description={<Caption1>For IT Administrator</Caption1>}
          />
          <div style={{ padding: tokens.spacingVerticalM }}>
            <Text family="monospace" size={200}>
              Error Code: {errorAnalysis.errorCode}<br/>
              Client ID: {clientId}<br/>
              Tenant ID: {tenantId || 'Not detected'}<br/>
              Teams Client ID: {teamsWebClientId}
            </Text>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ConsentManager;
